import { ExceptionEventState } from "@fairfleet/geotab";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExtWarning, warningIcons } from "./FaultInstructions";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useUpdateExceptionEvent } from "@/hooks/exceptionEvent/useUpdateExceptionEvent";
import { useFormattedDate } from "@/hooks/metrics/useFormattedDate";
import RulesAddinData from "@/pages/pmrules-edit/models/RulesAddinData";
import isDemo from "@/utilities/isDemo";

interface PMDetailProps {
  warning: ExtWarning;
  addin?: RulesAddinData;
}

type LocalState = "valid" | "dismissed" | "loading" | "error";

export default function PMDetail(props: PMDetailProps) {
  const { warning, addin } = props;
  const event = warning.event;
  const { activeFrom, activeTo } = event ?? {};
  const formattedFrom = useFormattedDate(activeFrom);
  const formattedTo = useFormattedDate(activeTo);
  const { t } = useTranslation();

  const updateEvent = useUpdateExceptionEvent("exceptionEvents");
  const { geotabClient } = useGeotabClient();

  const initialState: LocalState = useMemo(() => {
    const s = (event?.state as unknown as string) ?? "";
    if (s === "ExceptionEventStateValidId") return "valid";
    if (s === "ExceptionEventStateDismissedId") return "dismissed";
    return "dismissed";
  }, [event?.state]);

  const [localState, setLocalState] = useState<LocalState>(initialState);

  const handleUpdateEventState = async () => {
    if (!event || localState === "loading") return;

    const nextLocal: LocalState = localState === "valid" ? "dismissed" : "valid";
    const nextRemote: ExceptionEventState = (nextLocal === "valid"
      ? "ExceptionEventStateValidId"
      : "ExceptionEventStateDismissedId") as unknown as ExceptionEventState;

    try {
      setLocalState("loading");
      const result = await updateEvent.mutateAsync({
        geotabClient,
        entity: { ...event, state: nextRemote },
      } as any);

      if (result && typeof result === "object" && "error" in result) {
        setLocalState("error");
        alert(
          t(
            "faultOverview.updateFailed",
            "Aktualisierung fehlgeschlagen. Bitte später erneut versuchen.",
          ),
        );
        return;
      }

      setLocalState(nextLocal);
    } catch (err) {
      console.error(err);
      setLocalState("error");
      alert(
        t(
          "faultOverview.updateFailed",
          "Aktualisierung fehlgeschlagen. Bitte später erneut versuchen.",
        ),
      );
    }
  };

  const tooltipTitle = isDemo
    ? "Demo"
    : localState === "loading"
      ? t("faultOverview.loading", "Laden…")
      : localState === "error"
        ? t("faultOverview.error", "Fehler bei der Aktualisierung")
        : localState === "valid"
          ? t("faultOverview.dismissEvent", "Ereignis verwerfen")
          : t("faultOverview.validateEvent", "Ereignis bestätigen");

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {warningIcons[warning.type]}
        <Typography variant="h6" sx={{ ml: 1 }}>
          {addin?.pmTitle ?? ""}
        </Typography>

        <Tooltip title={tooltipTitle}>
          <IconButton
            onClick={handleUpdateEventState}
            disabled={isDemo || localState === "loading"}
            sx={{ ml: 1 }}
          >
            {localState === "loading" ? (
              <CircularProgress size={24} />
            ) : localState === "error" ? (
              <ErrorOutlineIcon color="warning" />
            ) : localState === "valid" ? (
              <CancelIcon color="error" />
            ) : (
              <CheckCircle color="success" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Typography>{addin?.pmDescription ?? ""}</Typography>

      <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("faultOverview.activeFrom")}:</strong> {formattedFrom}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("faultOverview.activeTo")}:</strong> {formattedTo}
        </Typography>
      </Box>
    </>
  );
}
